/**
 * Execute a formula+template transformation on a raw selection value.
 *
 * Variables available in the formula string:
 *   value — the original string extracted from the selection
 *   num   — the first numeric value found in `value` (or 0)
 *
 * Variables available in the template string:
 *   value  — the original string (before formula)
 *   result — the output of the formula
 *
 * @param {string} selectionValue  Raw text from the selection box / directText
 * @param {string} template        Output template string (may reference `value` and `result`)
 * @param {string} formula         JS expression string (may reference `value` and `num`)
 * @returns {string}               Final transformed text
 */
export function executeFormula (selectionValue, template, formula) {
  if (!template && !formula) return selectionValue

  try {
    let result = selectionValue

    if (formula) {
      try {
        const safeContext = {
          value: selectionValue,
          num: (() => {
            const m = selectionValue.match(/-?\d+(\.\d+)?/)
            return m ? parseFloat(m[0]) : 0
          })(),
          Math: {
            round: Math.round,
            floor: Math.floor,
            ceil: Math.ceil,
            abs: Math.abs,
            min: Math.min,
            max: Math.max,
            pow: Math.pow,
            sqrt: Math.sqrt
          },
          upper: (str) => String(str).toUpperCase(),
          lower: (str) => String(str).toLowerCase(),
          parseFloat,
          parseInt
        }

        let processedFormula = formula
        // round(x, n) convenience shorthand
        processedFormula = processedFormula.replace(
          /\bround\(([^,)]+),\s*(\d+)\)/g,
          'Math.round(($1) * Math.pow(10, $2)) / Math.pow(10, $2)'
        )
        processedFormula = processedFormula.replace(/\bround\(([^)]+)\)/g, 'Math.round($1)')

        const fn = new Function( // eslint-disable-line no-new-func
          ...Object.keys(safeContext),
          `"use strict"; return (${processedFormula});`
        )
        result = String(fn(...Object.values(safeContext)))
      } catch (err) {
        result = `Formula error: ${err.message}`
      }
    }

    if (template) {
      return template
        .replace(/\bvalue\b/g, selectionValue)
        .replace(/\bresult\b/g, result)
    }

    return result
  } catch (_) {
    return 'Preview error'
  }
}
